import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useFlag } from '@unleash/proxy-client-react';
import { GenericSidebarHeader } from 'components/layout/Sidebar';
import { useGetAsset } from 'features/assetControl/hooks/useGetAsset';
import { Icons } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useSidebarItems } from 'hooks/useSidebarItems';
import { canCreateAsset } from 'utils/authorization';
import { isBatteryAsset, isReportingOnly } from 'utils/units';

export function useAssetSidebar() {
  const { t } = useTranslation();
  const { dispatchSidebar } = useSidebarItems();
  const { unitExternalIdentifier = '' } = useParams();
  const theme = useTheme();
  const { user } = useAuth();
  const { asset } = useGetAsset(unitExternalIdentifier);
  const isReportingOnlyAsset = isReportingOnly(asset);
  const isBattery = isBatteryAsset(asset);
  const isHmiEnabled = useFlag('hmi-gui');

  useEffect(() => {
    if (!dispatchSidebar) {
      return;
    }

    dispatchSidebar({
      type: 'REGULAR',
      payload: {
        sidebarHeader: <GenericSidebarHeader title={t('unitControl:domain')} subtitle={unitExternalIdentifier} />,
        items: [
          {
            isVisible: !isReportingOnlyAsset,
            name: t('unitControl:navigation.unitDetails'),
            path: `/assets/${unitExternalIdentifier}`,
            icon: () => <Icons.UnitControl strokeColor={theme.palette.secondary.main} />,
          },
          {
            isVisible: !isReportingOnlyAsset,
            name: t('unitControl:navigation.graphs'),
            path: `/assets/${unitExternalIdentifier}/graphs`,
            icon: () => <Icons.Graphs strokeColor={theme.palette.secondary.main} />,
          },
          {
            isVisible: !isReportingOnlyAsset && canCreateAsset(user?.role),
            name: t('unitControl:navigation.multiBatteryGraphs'),
            path: `/assets/multi-battery-graphs?units=${unitExternalIdentifier}`,
            icon: () => <Icons.Graphs strokeColor={theme.palette.secondary.main} />,
          },
          {
            isVisible: isBattery && isHmiEnabled,
            name: t('unitControl:navigation.hmi'),
            path: `/assets/${unitExternalIdentifier}/hmi/overview`,
            icon: () => <Icons.Graphs strokeColor={theme.palette.secondary.main} />,
          },
          {
            isVisible: isReportingOnlyAsset,
            name: t('unitControl:navigation.reports'),
            path: `/assets/${unitExternalIdentifier}/reports`,
            icon: () => <Icons.Report strokeColor={theme.palette.secondary.main} />,
          },
        ].filter((item) => item.isVisible),
      },
    });
  }, [dispatchSidebar, unitExternalIdentifier, t, user, isReportingOnlyAsset, isBattery, isHmiEnabled]);
}
